import { gql } from '@apollo/client';
import { client } from '../apolloClient';
import { getApolloErrorMessage } from '../utils/apolloError';
import type { EditList } from '../utils/editing/editList';
import { normalizeEditList } from '../utils/editing/editList';

/**
 * Quick edits: edits, the brand kit, exports and the plan's allowance
 * (the API's src/graphql/editingSchema.ts). The edit list travels as JSON
 * text and is normalised on both sides.
 */

export interface EditSummary {
  id: string;
  title: string;
  sourceKind: 'video' | 'multicam';
  videoId: string | null;
  multicamSetId: string | null;
  version: number;
  summary: string;
  outputDurationMs: number;
  createdAt: string;
  updatedAt: string;
  lastExport: ExportRecord | null;
}

export interface EditRecord extends EditSummary {
  list: EditList;
}

export interface BrandAsset {
  id: string;
  kind: 'image' | 'audio' | 'captions';
  name: string;
  url: string;
  contentType: string;
  sizeBytes: number;
  durationMs: number | null;
  width: number | null;
  height: number | null;
  generated: boolean;
  createdAt: string;
}

export interface BrandKit {
  id: string;
  logoAssetId: string | null;
  displayName: string | null;
  primaryColor: string;
  assets: BrandAsset[];
  updatedAt: string;
}

export type ExportStatus = 'queued' | 'preparing' | 'rendering' | 'uploading' | 'done' | 'failed' | 'cancelled' | 'expired';

export interface ExportRecord {
  id: string;
  editId: string;
  editVersion: number;
  title: string;
  status: ExportStatus;
  progress: number;
  resolution: string;
  width: number | null;
  height: number | null;
  outputDurationMs: number;
  minutesCharged: number;
  sizeBytes: number | null;
  downloadUrl: string | null;
  sidecars: Array<{ name: string; url: string }>;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
  expiresAt: string | null;
}

export interface EditingAllowance {
  featureName: string;
  planId: string;
  enabled: boolean;
  exportMinutesPerMonth: number;
  exportMinutesUsed: number;
  aiPromptsPerMonth: number;
  aiPromptsUsed: number;
  maxResolution: '720p' | '1080p' | '2160p';
  maxExportMinutes1080p: number;
  maxExportMinutes4k: number;
  periodEndsAt: string;
  exportRetentionDays: number;
}

export const ACTIVE_EXPORT_STATUSES: ExportStatus[] = ['queued', 'preparing', 'rendering', 'uploading'];
export const isActiveExport = (status: ExportStatus): boolean => ACTIVE_EXPORT_STATUSES.indexOf(status) >= 0;

const EXPORT_FRAGMENT = gql`
  fragment ExportFields on Export {
    id
    editId
    editVersion
    title
    status
    progress
    resolution
    width
    height
    outputDurationMs
    minutesCharged
    sizeBytes
    downloadUrl
    sidecars {
      name
      url
    }
    error
    createdAt
    finishedAt
    expiresAt
  }
`;

const EDIT_FRAGMENT = gql`
  fragment EditFields on Edit {
    id
    title
    sourceKind
    videoId
    multicamSetId
    version
    listJson
    summary
    outputDurationMs
    createdAt
    updatedAt
    lastExport {
      ...ExportFields
    }
  }
  ${EXPORT_FRAGMENT}
`;

const KIT_FRAGMENT = gql`
  fragment BrandKitFields on BrandKit {
    id
    logoAssetId
    displayName
    primaryColor
    assets {
      id
      kind
      name
      url
      contentType
      sizeBytes
      durationMs
      width
      height
      generated
      createdAt
    }
    updatedAt
  }
`;

/** A GraphQL error with its code, so screens can tell a conflict from a plan limit. */
export class EditingApiError extends Error {
  code: string | null;
  extensions: Record<string, unknown>;

  constructor(error: unknown) {
    super(getApolloErrorMessage(error));
    this.name = 'EditingApiError';
    const first = (error as any)?.graphQLErrors?.[0];
    this.extensions = (first?.extensions as Record<string, unknown>) || {};
    this.code = typeof this.extensions.code === 'string' ? (this.extensions.code as string) : null;
  }
}

async function call<T>(operation: () => Promise<{ data?: T | null }>): Promise<T> {
  try {
    const { data } = await operation();
    if (!data) throw new Error('The server returned nothing.');
    return data;
  } catch (error) {
    throw new EditingApiError(error);
  }
}

function readEdit(raw: any): EditRecord {
  const parsed = JSON.parse(raw.listJson);
  const normalized = normalizeEditList(parsed);
  if (!normalized.ok) throw new Error('This edit couldn’t be read. Reload the page.');
  const { listJson, ...rest } = raw;
  return { ...rest, list: normalized.value };
}

export const fetchEdits = async (filter: { videoId?: string | null; multicamSetId?: string | null } = {}): Promise<EditSummary[]> => {
  const data = await call<{ edits: any[] }>(() => client.query({
    query: gql`
      query Edits($videoId: String, $multicamSetId: String) {
        edits(videoId: $videoId, multicamSetId: $multicamSetId) {
          ...EditFields
        }
      }
      ${EDIT_FRAGMENT}
    `,
    variables: filter,
    fetchPolicy: 'network-only',
  }));
  return data.edits.map(readEdit);
};

export const fetchEdit = async (id: string): Promise<EditRecord | null> => {
  const data = await call<{ edit: any }>(() => client.query({
    query: gql`
      query Edit($id: ID!) {
        edit(id: $id) {
          ...EditFields
        }
      }
      ${EDIT_FRAGMENT}
    `,
    variables: { id },
    fetchPolicy: 'network-only',
  }));
  return data.edit ? readEdit(data.edit) : null;
};

export const createEdit = async (input: { videoId?: string; multicamSetId?: string; title?: string }): Promise<EditRecord> => {
  const data = await call<{ createEdit: any }>(() => client.mutate({
    mutation: gql`
      mutation CreateEdit($videoId: String, $multicamSetId: String, $title: String) {
        createEdit(videoId: $videoId, multicamSetId: $multicamSetId, title: $title) {
          ...EditFields
        }
      }
      ${EDIT_FRAGMENT}
    `,
    variables: input,
  }));
  return readEdit(data.createEdit);
};

export const saveEdit = async (id: string, basedOnVersion: number, list: EditList, title?: string | null): Promise<EditRecord> => {
  const data = await call<{ saveEdit: any }>(() => client.mutate({
    mutation: gql`
      mutation SaveEdit($id: ID!, $basedOnVersion: Int!, $listJson: String!, $title: String) {
        saveEdit(id: $id, basedOnVersion: $basedOnVersion, listJson: $listJson, title: $title) {
          ...EditFields
        }
      }
      ${EDIT_FRAGMENT}
    `,
    variables: { id, basedOnVersion, listJson: JSON.stringify(list), title: title ?? null },
  }));
  return readEdit(data.saveEdit);
};

export const deleteEdit = async (id: string): Promise<boolean> => {
  const data = await call<{ deleteEdit: boolean }>(() => client.mutate({
    mutation: gql`
      mutation DeleteEdit($id: ID!) {
        deleteEdit(id: $id)
      }
    `,
    variables: { id },
  }));
  return data.deleteEdit;
};

export const fetchBrandKit = async (): Promise<BrandKit> => {
  const data = await call<{ brandKit: BrandKit }>(() => client.query({
    query: gql`
      query BrandKit {
        brandKit {
          ...BrandKitFields
        }
      }
      ${KIT_FRAGMENT}
    `,
    fetchPolicy: 'network-only',
  }));
  return data.brandKit;
};

export interface BrandAssetInput {
  kind: 'image' | 'audio' | 'captions';
  name: string;
  objectKey: string;
  url: string;
  contentType: string;
  sizeBytes: number;
  durationMs?: number | null;
  width?: number | null;
  height?: number | null;
}

export const addBrandAsset = async (input: BrandAssetInput): Promise<BrandKit> => {
  const data = await call<{ addBrandAsset: BrandKit }>(() => client.mutate({
    mutation: gql`
      mutation AddBrandAsset($input: BrandAssetInput!) {
        addBrandAsset(input: $input) {
          ...BrandKitFields
        }
      }
      ${KIT_FRAGMENT}
    `,
    variables: { input },
  }));
  return data.addBrandAsset;
};

export const removeBrandAsset = async (assetId: string): Promise<BrandKit> => {
  const data = await call<{ removeBrandAsset: BrandKit }>(() => client.mutate({
    mutation: gql`
      mutation RemoveBrandAsset($assetId: String!) {
        removeBrandAsset(assetId: $assetId) {
          ...BrandKitFields
        }
      }
      ${KIT_FRAGMENT}
    `,
    variables: { assetId },
  }));
  return data.removeBrandAsset;
};

export const updateBrandKit = async (input: { logoAssetId?: string | null; displayName?: string | null; primaryColor?: string | null }): Promise<BrandKit> => {
  const data = await call<{ updateBrandKit: BrandKit }>(() => client.mutate({
    mutation: gql`
      mutation UpdateBrandKit($logoAssetId: String, $clearLogo: Boolean, $displayName: String, $primaryColor: String) {
        updateBrandKit(logoAssetId: $logoAssetId, clearLogo: $clearLogo, displayName: $displayName, primaryColor: $primaryColor) {
          ...BrandKitFields
        }
      }
      ${KIT_FRAGMENT}
    `,
    variables: {
      logoAssetId: input.logoAssetId || null,
      clearLogo: input.logoAssetId === null,
      displayName: input.displayName,
      primaryColor: input.primaryColor,
    },
  }));
  return data.updateBrandKit;
};

export const startExport = async (editId: string): Promise<ExportRecord> => {
  const data = await call<{ startExport: ExportRecord }>(() => client.mutate({
    mutation: gql`
      mutation StartExport($editId: ID!) {
        startExport(editId: $editId) {
          ...ExportFields
        }
      }
      ${EXPORT_FRAGMENT}
    `,
    variables: { editId },
  }));
  return data.startExport;
};

export const cancelExport = async (id: string): Promise<ExportRecord> => {
  const data = await call<{ cancelExport: ExportRecord }>(() => client.mutate({
    mutation: gql`
      mutation CancelExport($id: ID!) {
        cancelExport(id: $id) {
          ...ExportFields
        }
      }
      ${EXPORT_FRAGMENT}
    `,
    variables: { id },
  }));
  return data.cancelExport;
};

export const fetchExports = async (editId?: string | null): Promise<ExportRecord[]> => {
  const data = await call<{ exports: ExportRecord[] }>(() => client.query({
    query: gql`
      query Exports($editId: String) {
        exports(editId: $editId) {
          ...ExportFields
        }
      }
      ${EXPORT_FRAGMENT}
    `,
    variables: { editId: editId ?? null },
    fetchPolicy: 'network-only',
  }));
  return data.exports;
};

export const fetchExport = async (id: string): Promise<ExportRecord | null> => {
  const data = await call<{ export: ExportRecord | null }>(() => client.query({
    query: gql`
      query ExportStatus($id: ID!) {
        export(id: $id) {
          ...ExportFields
        }
      }
      ${EXPORT_FRAGMENT}
    `,
    variables: { id },
    fetchPolicy: 'network-only',
  }));
  return data.export;
};

export const fetchEditingAllowance = async (): Promise<EditingAllowance> => {
  const data = await call<{ editingAllowance: EditingAllowance }>(() => client.query({
    query: gql`
      query EditingAllowance {
        editingAllowance {
          featureName
          planId
          enabled
          exportMinutesPerMonth
          exportMinutesUsed
          aiPromptsPerMonth
          aiPromptsUsed
          maxResolution
          maxExportMinutes1080p
          maxExportMinutes4k
          periodEndsAt
          exportRetentionDays
        }
      }
    `,
    fetchPolicy: 'network-only',
  }));
  return data.editingAllowance;
};

// --- Sending an edit to the editor -------------------------------------------

export interface EditTarget {
  id: string;
  projectName: string;
  status: string;
  editorName: string | null;
  /** The version of this edit already on the project, if any. */
  attachedEditVersion: number | null;
}

export interface EditHandoffResult {
  projectId: string;
  projectName: string;
  tasksAdded: number;
  tasksReplaced: number;
}

/** The creator's open projects made from the edit's video or set. */
export const fetchEditTargets = async (editId: string): Promise<EditTarget[]> => {
  const data = await call<{ editTargets: EditTarget[] }>(() => client.query({
    query: gql`
      query EditTargets($editId: ID!) {
        editTargets(editId: $editId) {
          id
          projectName
          status
          editorName
          attachedEditVersion
        }
      }
    `,
    variables: { editId },
    fetchPolicy: 'network-only',
  }));
  return data.editTargets;
};

/** Puts the edit's work on a project as tasks (replacing an earlier send of the same edit). */
export const sendEditToProject = async (editId: string, projectId: string): Promise<EditHandoffResult> => {
  const data = await call<{ sendEditToProject: EditHandoffResult }>(() => client.mutate({
    mutation: gql`
      mutation SendEditToProject($editId: ID!, $projectId: ID!) {
        sendEditToProject(editId: $editId, projectId: $projectId) {
          projectId
          projectName
          tasksAdded
          tasksReplaced
        }
      }
    `,
    variables: { editId, projectId },
  }));
  return data.sendEditToProject;
};

// --- The AI command bar ---------------------------------------------------------

export interface AiTurn {
  instruction: string;
  reply: string;
}

export interface AiEditResult {
  instruction: string;
  reply: string;
  list: EditList;
  changed: boolean;
  promptsLeft: number;
  provider: string;
  /** A built-in stand-in answered (no AI key configured; local development). */
  offline: boolean;
}

export interface ShortSuggestion {
  title: string;
  fromMs: number;
  toMs: number;
  reason: string;
}

export interface AiStatus {
  available: boolean;
  provider: string;
  offline: boolean;
  speech: boolean;
  voices: string[];
  promptsPerMonth: number;
  promptsUsed: number;
  periodEndsAt: string;
}

export const fetchAiStatus = async (): Promise<AiStatus> => {
  const data = await call<{ aiStatus: AiStatus }>(() => client.query({
    query: gql`
      query AiStatus {
        aiStatus {
          available
          provider
          offline
          speech
          voices
          promptsPerMonth
          promptsUsed
          periodEndsAt
        }
      }
    `,
    fetchPolicy: 'network-only',
  }));
  return data.aiStatus;
};

/** Asks the brain; the answer is a whole list to apply (undoable), nothing is saved. */
export const aiEdit = async (editId: string, input: { instruction?: string; audioBase64?: string; audioContentType?: string; history?: AiTurn[] }): Promise<AiEditResult> => {
  const data = await call<{ aiEdit: any }>(() => client.mutate({
    mutation: gql`
      mutation AiEdit($editId: ID!, $instruction: String, $audioBase64: String, $audioContentType: String, $history: [AiTurnInput!]) {
        aiEdit(editId: $editId, instruction: $instruction, audioBase64: $audioBase64, audioContentType: $audioContentType, history: $history) {
          instruction
          reply
          listJson
          changed
          promptsLeft
          provider
          offline
        }
      }
    `,
    variables: { editId, instruction: input.instruction ?? null, audioBase64: input.audioBase64 ?? null, audioContentType: input.audioContentType ?? null, history: input.history ?? [] },
  }));
  const raw = data.aiEdit;
  const normalized = normalizeEditList(JSON.parse(raw.listJson));
  if (!normalized.ok) throw new Error('The AI’s answer couldn’t be read. Try again.');
  const { listJson, ...rest } = raw;
  return { ...rest, list: normalized.value };
};

export const suggestShorts = async (editId: string, input: { count?: number; targetSeconds?: number; about?: string | null } = {}): Promise<{ shorts: ShortSuggestion[]; promptsLeft: number }> => {
  const data = await call<{ suggestShorts: { shorts: ShortSuggestion[]; promptsLeft: number } }>(() => client.mutate({
    mutation: gql`
      mutation SuggestShorts($editId: ID!, $count: Int, $targetSeconds: Int, $about: String) {
        suggestShorts(editId: $editId, count: $count, targetSeconds: $targetSeconds, about: $about) {
          shorts {
            title
            fromMs
            toMs
            reason
          }
          promptsLeft
        }
      }
    `,
    variables: { editId, count: input.count ?? 3, targetSeconds: input.targetSeconds ?? 45, about: input.about ?? null },
  }));
  return data.suggestShorts;
};

/** Speaks a TTS line of the edit and saves the new version (the line then points at its file). */
export const speakVoiceover = async (editId: string, basedOnVersion: number, voiceoverId: string): Promise<EditRecord> => {
  const data = await call<{ speakVoiceover: any }>(() => client.mutate({
    mutation: gql`
      mutation SpeakVoiceover($editId: ID!, $basedOnVersion: Int!, $voiceoverId: String!) {
        speakVoiceover(editId: $editId, basedOnVersion: $basedOnVersion, voiceoverId: $voiceoverId) {
          ...EditFields
        }
      }
      ${EDIT_FRAGMENT}
    `,
    variables: { editId, basedOnVersion, voiceoverId },
  }));
  return readEdit(data.speakVoiceover);
};
